import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { SharedWorkoutPreview, WorkoutShareWithDetails, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workout-shares-tabpanel-${index}`}
      aria-labelledby={`workout-shares-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface SharedWorkoutsViewProps {
  fetchWithAuth: FetchWithAuth;
}

const SharedWorkoutsView: React.FC<SharedWorkoutsViewProps> = ({ fetchWithAuth }) => {
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(0);
  const [receivedShares, setReceivedShares] = useState<SharedWorkoutPreview[]>([]);
  const [sentShares, setSentShares] = useState<WorkoutShareWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    workout: SharedWorkoutPreview | null;
  }>({ open: false, workout: null });
  const [targetDate, setTargetDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState<string | null>(null);

  const loadSharedWorkouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [receivedResponse, sentResponse] = await Promise.all([
        fetchWithAuth(`${API_URL}/workout-shares/received`),
        fetchWithAuth(`${API_URL}/workout-shares/sent`)
      ]);

      if (receivedResponse.ok && sentResponse.ok) {
        const receivedData = await receivedResponse.json();
        const sentData = await sentResponse.json();
        setReceivedShares(Array.isArray(receivedData) ? receivedData : []);
        setSentShares(Array.isArray(sentData) ? sentData : []);
      } else {
        throw new Error('Failed to load shared workouts');
      }
    } catch (err) {
      console.error('Error loading shared workouts:', err);
      setError(t('workoutShare.loadSharedError'));
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t]);

  useEffect(() => {
    loadSharedWorkouts();
  }, [loadSharedWorkouts]);

  const handlePreview = (workout: SharedWorkoutPreview) => {
    setPreviewDialog({ open: true, workout });
  };

  const handleClosePreview = () => {
    setPreviewDialog({ open: false, workout: null });
  };

  const handleAccept = async (shareId: number) => {
    if (!targetDate) {
      setError(t('workoutShare.selectDate'));
      return;
    }

    setActionLoading(shareId);
    try {
      const response = await fetchWithAuth(`${API_URL}/workout-shares/${shareId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetDate })
      });

      const data = await response.json();

      if (data.success) {
        await loadSharedWorkouts();
        handleClosePreview();
      } else {
        setError(data.message || t('workoutShare.acceptError'));
      }
    } catch (err) {
      console.error('Error accepting workout:', err);
      setError(t('workoutShare.acceptError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (shareId: number) => {
    setActionLoading(shareId);
    try {
      const response = await fetchWithAuth(`${API_URL}/workout-shares/${shareId}/decline`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        await loadSharedWorkouts();
        handleClosePreview();
      } else {
        setError(data.message || t('workoutShare.declineError'));
      }
    } catch (err) {
      console.error('Error declining workout:', err);
      setError(t('workoutShare.declineError'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="workout shares tabs"
          variant="fullWidth"
        >
          <Tab label={t('workoutShare.receivedShares')} />
          <Tab label={t('workoutShare.sentShares')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : receivedShares.length === 0 ? (
            <Alert severity="info">{t('workoutShare.noReceivedShares')}</Alert>
          ) : (
            <List>
              {receivedShares.map((share) => (
                <Paper key={share.id} sx={{ mb: 2, p: 2 }}>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('workoutShare.sharedBy')}: {share.shared_by_user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(share.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('workoutShare.exercises')}: {share.exercises.length}
                      </Typography>
                      {share.message && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          "{share.message}"
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreview(share)}
                      >
                        {t('workoutShare.preview')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => handlePreview(share)}
                        disabled={actionLoading === share.id}
                      >
                        {t('workoutShare.accept')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={() => handleDecline(share.id)}
                        disabled={actionLoading === share.id}
                      >
                        {t('workoutShare.decline')}
                      </Button>
                    </Box>
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sentShares.length === 0 ? (
            <Alert severity="info">{t('workoutShare.noSentShares')}</Alert>
          ) : (
            <List>
              {sentShares.map((share) => (
                <Paper key={share.id} sx={{ mb: 2, p: 2 }}>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('workoutShare.sharedWith')}: {share.shared_with_user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(share.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('workoutShare.exercises')}: {share.exercises.length}
                      </Typography>
                      {share.message && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          "{share.message}"
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={
                        share.status === 'pending'
                          ? t('workoutShare.pending')
                          : share.status === 'accepted'
                          ? t('workoutShare.accepted')
                          : t('workoutShare.declined')
                      }
                      color={
                        share.status === 'pending'
                          ? 'warning'
                          : share.status === 'accepted'
                          ? 'success'
                          : 'error'
                      }
                      size="small"
                    />
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('workoutShare.previewWorkout')}</DialogTitle>
        <DialogContent>
          {previewDialog.workout && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('workoutShare.sharedBy')}: {previewDialog.workout.shared_by_user.name}
              </Typography>
              {previewDialog.workout.message && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {previewDialog.workout.message}
                </Alert>
              )}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                {t('workoutShare.exercises')}:
              </Typography>
              <List dense>
                {previewDialog.workout.exercises.map((exercise, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={exercise.exercise_name}
                      secondary={`${exercise.sets} ${t('workoutShare.sets')} x ${exercise.reps} ${t('workoutShare.reps')}`}
                    />
                  </ListItem>
                ))}
              </List>
              <TextField
                fullWidth
                type="date"
                label={t('workoutShare.targetDate')}
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                sx={{ mt: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('workoutShare.acceptInfo')}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} disabled={actionLoading !== null}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => previewDialog.workout && handleDecline(previewDialog.workout.id)}
            color="error"
            disabled={actionLoading !== null}
          >
            {t('workoutShare.decline')}
          </Button>
          <Button
            onClick={() => previewDialog.workout && handleAccept(previewDialog.workout.id)}
            variant="contained"
            color="success"
            disabled={actionLoading !== null || !targetDate}
          >
            {actionLoading !== null ? t('common.loading') : t('workoutShare.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedWorkoutsView;
